import React, { useState, useRef } from 'react';
import { IonItem, IonList, IonSpinner } from '@ionic/react';
import { api } from '@/utils/api';
// @ts-ignore - Using lodash/debounce without types
import debounce from 'lodash/debounce';
import { t } from 'i18next';

interface HashtagInputProps {
  value: string;
  onChange: (value: string) => void;
  onHashtagSelect?: (hashtag: string) => void;
}

interface Hashtag {
  id: number;
  name: string;
  count: number;
}

const HashtagInput: React.FC<HashtagInputProps> = ({ value, onChange, onHashtagSelect }) => {
  const [suggestions, setSuggestions] = useState<Hashtag[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Find hashtag at cursor position
  const findHashtagAtCursor = (text: string, position: number) => {
    // Find the start of the hashtag (# character)
    let start = position;
    while (start > 0 && text[start - 1] !== ' ' && text[start - 1] !== '\n' && text[start - 1] !== '#') {
      start--;
    }

    // Check if we found a hashtag
    if (start > 0 && text[start - 1] === '#') {
      start--; // Include the # character

      // Find the end of the hashtag (space or end of text)
      let end = position;
      while (end < text.length && text[end] !== ' ' && text[end] !== '\n') {
        end++;
      }

      return {
        hashtag: text.substring(start, end),
        start,
        end
      };
    }

    return null;
  };

  // Format text with colored hashtags
  const formatTextWithHashtags = (text: string) => {
    if (!text) return [];

    const hashtagRegex = /#[\w]+/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = hashtagRegex.exec(text)) !== null) {
      // Add text before the hashtag
      if (match.index > lastIndex) {
        parts.push({
          text: text.substring(lastIndex, match.index),
          isHashtag: false
        });
      }

      // Add the hashtag
      parts.push({
        text: match[0],
        isHashtag: true
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        text: text.substring(lastIndex),
        isHashtag: false
      });
    }

    return parts;
  };

  // Debounced search function
  const searchHashtags = debounce(async (query: string) => {
    if (!query || query === '#') {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      setLoading(true);
      const { data } = await api.get(`/hashtags/search?q=${encodeURIComponent(query.substring(1))}`);
      setSuggestions(data);
      // Only show suggestions if we have results
      setShowSuggestions(data.length > 0);
    } catch (error) {
      console.error('Error searching hashtags:', error);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  }, 300);

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const newPosition = e.target.selectionStart || 0;

    onChange(newValue);
    setCursorPosition(newPosition);

    // Check if we're typing a hashtag
    const hashtagInfo = findHashtagAtCursor(newValue, newPosition);

    if (hashtagInfo && hashtagInfo.hashtag.length > 1) {
      searchHashtags(hashtagInfo.hashtag);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // Handle selection of a hashtag from suggestions
  const handleSelectHashtag = (hashtag: Hashtag) => {
    if (!textareaRef.current) return;

    const hashtagInfo = findHashtagAtCursor(value, cursorPosition);
    if (!hashtagInfo) return;

    // Replace the current hashtag with the selected one
    const newValue =
      value.substring(0, hashtagInfo.start) +
      `#${hashtag.name}` +
      value.substring(hashtagInfo.end);

    onChange(newValue);

    // Calculate new cursor position after the inserted hashtag
    const newPosition = hashtagInfo.start + hashtag.name.length + 1;

    // Set the new cursor position
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPosition, newPosition);
        setCursorPosition(newPosition);
      }
    }, 0);

    setShowSuggestions(false);

    if (onHashtagSelect) {
      onHashtagSelect(hashtag.name);
    }
  };

  return (
    <div className="relative w-full">
      <div className="relative w-full max-h-96 min-h-24 border-none bg-transparent shadow-none resize-none p-2 focus:outline-none overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none p-2 whitespace-pre-wrap break-words"
          style={{ visibility: value ? 'visible' : 'hidden' }}
        >
          {formatTextWithHashtags(value).map((part, index) => (
            <span
              key={index}
              className={part.isHashtag ? 'text-green-600 font-medium' : ''}
            >
              {part.text}
            </span>
          ))}

        </div>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setShowSuggestions(false);
            }
          }}
          onClick={(e) => {
            const target = e.target as HTMLTextAreaElement;
            setCursorPosition(target.selectionStart || 0);

            // Check if we clicked on a hashtag
            const hashtagInfo = findHashtagAtCursor(value, target.selectionStart || 0);
            if (hashtagInfo && hashtagInfo.hashtag.length > 1) {
              searchHashtags(hashtagInfo.hashtag);
            } else {
              setShowSuggestions(false);
            }
          }}
          className="absolute inset-0 w-full h-full bg-transparent resize-none p-2 focus:outline-none text-transparent caret-black dark:caret-white"
          placeholder={t('whats_up') + ' ?'}
        />
      </div>

      {showSuggestions && (
        <div className="absolute z-10 w-full max-w-md bg-white dark:bg-gray-800 rounded-md shadow-lg mt-1 border border-gray-200 dark:border-gray-700 max-h-60 overflow-auto">
          {loading ? (
            <div className="flex justify-center p-4">
              <IonSpinner name="crescent" />
            </div>
          ) : suggestions.length > 0 ? (
            <IonList className="p-0">
              {suggestions.map((hashtag) => (
                <IonItem
                  key={hashtag.id}
                  button
                  detail={false}
                  onClick={() => handleSelectHashtag(hashtag)}
                  className="cursor-pointer"
                >
                  <div className="flex items-center justify-between w-full py-2">
                    <div className="font-medium">#{hashtag.name}</div>
                    <div className="text-sm text-gray-500">{hashtag.count} posts</div>
                  </div>
                </IonItem>
              ))}
            </IonList>
          ) : (
            <div className="p-4 text-center text-gray-500">
              No hashtags found. This will create a new one.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HashtagInput;
